import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ImageUpload, IMAGE_FORMATS } from '../components/ui/ImageUpload';
import { ChevronRight, User, AlertCircle, Check, Clock, Info, Lock, Eye, EyeOff, Star, Share2, Instagram, Facebook, Youtube, Linkedin, Twitter, Send } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { GoogleRatingInput } from './GoogleRatingInput';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

// Social platforms config
const SOCIAL_PLATFORMS = [
  { key: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/tu_perfil', color: 'from-purple-500 to-pink-500' },
  { key: 'facebook', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/tu_perfil', color: 'bg-blue-600' },
  { key: 'youtube', label: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/@tu_canal', color: 'bg-red-600' },
  { key: 'tiktok', label: 'TikTok', icon: TikTokIcon, placeholder: 'https://tiktok.com/@tu_perfil', color: 'bg-black' },
  { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/in/tu_perfil', color: 'bg-blue-700' },
  { key: 'twitter', label: 'Twitter / X', icon: Twitter, placeholder: 'https://twitter.com/tu_perfil', color: 'bg-sky-500' },
  { key: 'telegram', label: 'Telegram', icon: Send, placeholder: 'https://t.me/tu_perfil', color: 'bg-sky-400' },
];

export function EditProfilePage() {
  const { t, i18n } = useTranslation();
  const { user, getAuthHeaders, loadUser } = useAuthStore();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [language, setLanguage] = useState('es');
  const [avatar, setAvatar] = useState(null);
  const [originalAvatar, setOriginalAvatar] = useState(null);
  
  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // Google Rating state (for teachers/schools)
  const [showGoogleRatingSection, setShowGoogleRatingSection] = useState(false);
  const [teacherOrSchool, setTeacherOrSchool] = useState(null);
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [googleRating, setGoogleRating] = useState(null);
  const [googleReviewsCount, setGoogleReviewsCount] = useState(null);
  const [isGoogleRatingVerified, setIsGoogleRatingVerified] = useState(false);
  const [isSavingGoogleRating, setIsSavingGoogleRating] = useState(false);
  const [googleRatingSuccess, setGoogleRatingSuccess] = useState('');
  
  // Social Links state (for teachers/schools)
  const [showSocialLinksSection, setShowSocialLinksSection] = useState(false);
  const [socialLinks, setSocialLinks] = useState({
    instagram: '', facebook: '', youtube: '', tiktok: '', linkedin: '', twitter: '', telegram: ''
  });
  const [isSavingSocialLinks, setIsSavingSocialLinks] = useState(false);
  const [socialLinksSuccess, setSocialLinksSuccess] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadProfile();
  }, [user, navigate]);

  const loadProfile = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/auth/profile`, {
        headers: getAuthHeaders()
      });
      const data = response.data;
      setProfile(data);
      setName(data.name || '');
      setPhone(data.phone || '');
      setLanguage(data.preferred_language || 'es');
      setAvatar(data.avatar || null);
      setOriginalAvatar(data.avatar || null);
      
      // Load teacher or school data if user has those roles
      if (data.roles?.includes('teacher')) {
        try {
          const teacherRes = await axios.get(`${API_URL}/api/teachers/me/profile`, {
            headers: getAuthHeaders()
          });
          if (teacherRes.data) {
            setTeacherOrSchool({ type: 'teacher', data: teacherRes.data });
            const gbr = teacherRes.data.google_business_rating || {};
            setGoogleMapsUrl(gbr.google_maps_url || '');
            setGoogleRating(gbr.google_rating || null);
            setGoogleReviewsCount(gbr.google_reviews_count || null);
            setIsGoogleRatingVerified(gbr.is_verified || false);
            // Load social links
            const sl = teacherRes.data.social_links || {};
            setSocialLinks({
              instagram: sl.instagram || '',
              facebook: sl.facebook || '',
              youtube: sl.youtube || '',
              tiktok: sl.tiktok || '',
              linkedin: sl.linkedin || '',
              twitter: sl.twitter || '',
              telegram: sl.telegram || ''
            });
          }
        } catch (e) {
          console.log('No teacher profile found');
        }
      } else if (data.roles?.includes('school')) {
        try {
          const schoolRes = await axios.get(`${API_URL}/api/schools/me/profile`, {
            headers: getAuthHeaders()
          });
          if (schoolRes.data) {
            setTeacherOrSchool({ type: 'school', data: schoolRes.data });
            const gbr = schoolRes.data.google_business_rating || {};
            setGoogleMapsUrl(gbr.google_maps_url || '');
            setGoogleRating(gbr.google_rating || null);
            setGoogleReviewsCount(gbr.google_reviews_count || null);
            setIsGoogleRatingVerified(gbr.is_verified || false);
            // Load social links
            const sl = schoolRes.data.social_links || {};
            setSocialLinks({
              instagram: sl.instagram || '',
              facebook: sl.facebook || '',
              youtube: sl.youtube || '',
              tiktok: sl.tiktok || '',
              linkedin: sl.linkedin || '',
              twitter: sl.twitter || '',
              telegram: sl.telegram || ''
            });
          }
        } catch (e) {
          console.log('No school profile found');
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
    setIsLoading(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSuccessMessage('');
    
    try {
      const updateData = {
        name,
        phone: phone || null,
        preferred_language: language,
      };
      
      // Only include avatar if it changed
      const avatarChanged = avatar !== originalAvatar;
      if (avatarChanged) {
        updateData.avatar = avatar;
      }
      
      const response = await axios.put(
        `${API_URL}/api/auth/profile`,
        updateData,
        { headers: getAuthHeaders() }
      );
      
      // Reload user in store
      await loadUser();
      
      // Navigate back to profile page after successful save
      navigate('/profile');
      
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar el perfil');
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    
    if (newPassword.length < 8) {
      setPasswordError('La nueva contraseña debe tener al menos 8 caracteres');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }
    
    setIsChangingPassword(true);
    
    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        { headers: getAuthHeaders() }
      );
      
      setPasswordSuccess('¡Contraseña actualizada correctamente!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Error al cambiar la contraseña');
    }
    
    setIsChangingPassword(false);
  };

  const handleSaveGoogleRating = async () => {
    if (!teacherOrSchool) return;
    
    setIsSavingGoogleRating(true);
    setGoogleRatingSuccess('');
    
    try {
      const endpoint = teacherOrSchool.type === 'teacher' 
        ? `${API_URL}/api/teachers/${teacherOrSchool.data.id}`
        : `${API_URL}/api/schools/${teacherOrSchool.data.id}`;
      
      const updateData = {
        google_business_rating: {
          google_maps_url: googleMapsUrl || null,
          google_rating: googleRating || null,
          google_reviews_count: googleReviewsCount || null
        }
      };
      
      await axios.put(endpoint, updateData, { headers: getAuthHeaders() });
      
      // Navigate back to profile page after successful save
      navigate('/profile');
      
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al guardar');
      setIsSavingGoogleRating(false);
    }
  };

  const handleSaveSocialLinks = async () => {
    if (!teacherOrSchool) return;
    
    setIsSavingSocialLinks(true);
    setSocialLinksSuccess('');
    
    try {
      const endpoint = teacherOrSchool.type === 'teacher' 
        ? `${API_URL}/api/teachers/${teacherOrSchool.data.id}`
        : `${API_URL}/api/schools/${teacherOrSchool.data.id}`;
      
      const updateData = {
        social_links: {
          instagram: socialLinks.instagram || null,
          facebook: socialLinks.facebook || null,
          youtube: socialLinks.youtube || null,
          tiktok: socialLinks.tiktok || null,
          linkedin: socialLinks.linkedin || null,
          twitter: socialLinks.twitter || null,
          telegram: socialLinks.telegram || null
        }
      };
      
      await axios.put(endpoint, updateData, { headers: getAuthHeaders() });
      
      setSocialLinksSuccess(t('socialLinks.savedSuccess', '¡Redes sociales actualizadas!'));
      
      // Clear success message after 3 seconds
      setTimeout(() => setSocialLinksSuccess(''), 3000);
      
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al guardar');
    }
    
    setIsSavingSocialLinks(false);
  };

  const updateSocialLink = (key, value) => {
    setSocialLinks(prev => ({ ...prev, [key]: value }));
  };

  const getActiveSocialLinksCount = () => {
    return Object.values(socialLinks).filter(v => v && v.trim()).length;
  };

  const getAvatarStatusBadge = () => {
    if (!profile) return null;
    
    const status = profile.avatar_status;
    if (status === 'pending') {
      return (
        <Badge className="bg-yellow-500 text-white flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Photo Pending Approval
        </Badge>
      );
    } else if (status === 'rejected') {
      return (
        <Badge className="bg-red-500 text-white flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Photo Rejected
        </Badge>
      );
    } else if (status === 'approved' && profile.avatar) {
      return (
        <Badge className="bg-green-500 text-white flex items-center gap-1">
          <Check className="w-3 h-3" />
          Photo Approved
        </Badge>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#D4A574', borderTopColor: 'transparent' }} />
          <p className="text-secondary">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 py-8 animate-fade-in" data-testid="edit-profile-page">
      <button onClick={() => navigate('/profile')} className="flex items-center text-secondary mb-6 hover:text-gold">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> Back to Profile
      </button>
      
      <h1 className="font-serif text-3xl font-bold text-primary mb-6">Edit Profile</h1>
      
      {/* Success Message */}
      {successMessage && (
        <div className="flex gap-3 p-4 rounded-xl mb-6 bg-green-50 border border-green-200">
          <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-700">{successMessage}</p>
        </div>
      )}
      
      {/* Avatar Status Info */}
      {profile?.avatar_status === 'pending' && (
        <div className="flex gap-3 p-4 rounded-xl mb-6" style={{ backgroundColor: '#FEF3C7' }}>
          <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0" />
          <div>
            <p className="text-sm text-yellow-800 font-medium">Photo Pending Approval</p>
            <p className="text-sm text-yellow-700">Your new profile photo is waiting for admin review. Your current photo will remain visible until the new one is approved.</p>
          </div>
        </div>
      )}
      
      {profile?.avatar_status === 'rejected' && (
        <div className="flex gap-3 p-4 rounded-xl mb-6" style={{ backgroundColor: '#FEE2E2' }}>
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div>
            <p className="text-sm text-red-800 font-medium">Photo Was Rejected</p>
            <p className="text-sm text-red-700">Your submitted photo was not approved. Please upload a different photo that follows our guidelines.</p>
          </div>
        </div>
      )}
      
      <div className="bg-white rounded-2xl shadow-sm p-6 space-y-6">
        {/* Profile Photo */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-primary">Profile Photo</label>
            {getAvatarStatusBadge()}
          </div>
          
          {/* Show pending avatar preview if exists */}
          {profile?.pending_avatar && profile?.avatar_status === 'pending' && (
            <div className="mb-4 p-3 bg-yellow-50 rounded-xl">
              <p className="text-xs text-yellow-700 mb-2">Pending approval:</p>
              <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-yellow-400">
                <img src={profile.pending_avatar} alt="Pending" className="w-full h-full object-cover" />
              </div>
            </div>
          )}
          
          <ImageUpload
            image={avatar}
            onImageChange={setAvatar}
            aspectRatio={IMAGE_FORMATS.PROFILE.aspectRatio}
            maxSize={IMAGE_FORMATS.PROFILE.maxSize}
            shape="circle"
            placeholder="Add your photo"
            folder="users/"
          />
          
          {avatar !== originalAvatar && avatar && (
            <div className="flex gap-2 items-center mt-2 p-2 bg-blue-50 rounded-lg">
              <Info className="w-4 h-4 text-blue-500" />
              <p className="text-xs text-blue-700">New photo will require admin approval</p>
            </div>
          )}
        </div>
        
        {/* Name */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Name *</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            required
            data-testid="edit-name"
          />
        </div>
        
        {/* Email (read-only) */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Email</label>
          <Input
            value={profile?.email || ''}
            disabled
            className="bg-gray-50 text-secondary"
            data-testid="edit-email"
          />
          <p className="text-xs text-secondary mt-1">Email cannot be changed</p>
        </div>
        
        {/* Phone */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Phone</label>
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+34 612 345 678"
            data-testid="edit-phone"
          />
        </div>
        
        {/* Language */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Preferred Language</label>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger data-testid="edit-language">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="es">Español</SelectItem>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="it">Italiano</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Role Info */}
        <div className="pt-4 border-t">
          <label className="block text-sm font-semibold text-primary mb-2">Account Type</label>
          <Badge variant="outline" className="text-sm">
            {profile?.role === 'admin' ? 'Administrator' : 
             profile?.role === 'teacher' ? 'Teacher' :
             profile?.role === 'school' ? 'School' : 'User'}
          </Badge>
        </div>
        
        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={isSaving || !name.trim()}
          className="w-full bg-gold hover:bg-gold/90 text-white py-3"
          style={{ backgroundColor: '#D4A574' }}
          data-testid="save-profile-btn"
        >
          {isSaving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </div>
      
      {/* Change Password Section */}
      <div className="bg-white rounded-2xl shadow-sm p-6 mt-6">
        <button 
          onClick={() => setShowPasswordSection(!showPasswordSection)}
          className="flex items-center justify-between w-full"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <Lock className="h-5 w-5 text-gray-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-primary">Cambiar Contraseña</h3>
              <p className="text-sm text-secondary">Actualiza tu contraseña de acceso</p>
            </div>
          </div>
          <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${showPasswordSection ? 'rotate-90' : ''}`} />
        </button>
        
        {showPasswordSection && (
          <div className="mt-6 pt-6 border-t space-y-4">
            {passwordSuccess && (
              <div className="flex gap-3 p-4 rounded-xl bg-green-50 border border-green-200">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-700">{passwordSuccess}</p>
              </div>
            )}
            
            {passwordError && (
              <div className="flex gap-3 p-4 rounded-xl bg-red-50 border border-red-200">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-700">{passwordError}</p>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">Contraseña Actual</label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">Nueva Contraseña</label>
              <div className="relative">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  data-testid="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">Confirmar Nueva Contraseña</label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite la nueva contraseña"
                data-testid="confirm-password"
              />
            </div>
            
            <Button
              onClick={handleChangePassword}
              disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword}
              className="w-full bg-gray-800 hover:bg-gray-900 text-white py-3"
              data-testid="change-password-btn"
            >
              {isChangingPassword ? 'Cambiando...' : 'Cambiar Contraseña'}
            </Button>
          </div>
        )}
      </div>
      
      {/* Google Business Rating Section (for teachers/schools) */}
      {teacherOrSchool && (
        <div className="bg-white rounded-2xl shadow-sm p-6 mt-6">
          <button 
            onClick={() => setShowGoogleRatingSection(!showGoogleRatingSection)}
            className="flex items-center justify-between w-full"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <Star className="h-5 w-5 text-yellow-500" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-primary">{t('googleRating.title', 'Google Business Rating')}</h3>
                <p className="text-sm text-secondary">
                  {isGoogleRatingVerified 
                    ? t('googleRating.verified', 'Verificado')
                    : googleRating 
                      ? t('googleRating.pendingVerification', 'Pendiente de verificación')
                      : t('googleRating.addRating', 'Añade tu valoración de Google')
                  }
                </p>
              </div>
            </div>
            <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${showGoogleRatingSection ? 'rotate-90' : ''}`} />
          </button>
          
          {showGoogleRatingSection && (
            <div className="mt-6 pt-6 border-t space-y-4">
              {googleRatingSuccess && (
                <div className="flex gap-3 p-4 rounded-xl bg-green-50 border border-green-200">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <p className="text-sm text-green-700">{googleRatingSuccess}</p>
                </div>
              )}
              
              <GoogleRatingInput
                googleMapsUrl={googleMapsUrl}
                setGoogleMapsUrl={setGoogleMapsUrl}
                googleRating={googleRating}
                setGoogleRating={setGoogleRating}
                googleReviewsCount={googleReviewsCount}
                setGoogleReviewsCount={setGoogleReviewsCount}
                isVerified={isGoogleRatingVerified}
                showVerificationStatus={true}
              />
              
              <Button
                onClick={handleSaveGoogleRating}
                disabled={isSavingGoogleRating}
                className="w-full bg-gold hover:bg-gold/90 text-white py-3"
                style={{ backgroundColor: '#D4A574' }}
                data-testid="save-google-rating-btn"
              >
                {isSavingGoogleRating ? t('common.saving', 'Guardando...') : t('googleRating.save', 'Guardar Valoración Google')}
              </Button>
            </div>
          )}
        </div>
      )}
      
      {/* Social Links Section (for teachers/schools) */}
      {teacherOrSchool && (
        <div className="bg-white rounded-2xl shadow-sm p-6 mt-6">
          <button 
            onClick={() => setShowSocialLinksSection(!showSocialLinksSection)}
            className="flex items-center justify-between w-full"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                <Share2 className="h-5 w-5 text-purple-500" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-primary">{t('socialLinks.title', 'Redes Sociales')}</h3>
                <p className="text-sm text-secondary">
                  {getActiveSocialLinksCount() > 0
                    ? t('socialLinks.activeCount', '{{count}} redes configuradas', { count: getActiveSocialLinksCount() })
                    : t('socialLinks.addLinks', 'Añade tus redes sociales')
                  }
                </p>
              </div>
            </div>
            <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${showSocialLinksSection ? 'rotate-90' : ''}`} />
          </button>
          
          {showSocialLinksSection && (
            <div className="mt-6 pt-6 border-t space-y-4">
              {socialLinksSuccess && (
                <div className="flex gap-3 p-4 rounded-xl bg-green-50 border border-green-200">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <p className="text-sm text-green-700">{socialLinksSuccess}</p>
                </div>
              )}
              
              <p className="text-sm text-secondary mb-4">
                {t('socialLinks.description', 'Añade los enlaces a tus redes sociales para que los visitantes puedan seguirte. Solo se mostrarán las redes que configures.')}
              </p>
              
              <div className="space-y-3">
                {SOCIAL_PLATFORMS.map(({ key, label, icon: Icon, placeholder }) => (
                  <div key={key} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5 text-gray-600" />
                    </div>
                    <div className="flex-1">
                      <Input
                        value={socialLinks[key]}
                        onChange={(e) => updateSocialLink(key, e.target.value)}
                        placeholder={placeholder}
                        type="url"
                        className="w-full"
                      />
                    </div>
                  </div>
                ))}
              </div>
              
              <Button
                onClick={handleSaveSocialLinks}
                disabled={isSavingSocialLinks}
                className="w-full bg-gold hover:bg-gold/90 text-white py-3 mt-4"
                style={{ backgroundColor: '#D4A574' }}
                data-testid="save-social-links-btn"
              >
                {isSavingSocialLinks ? t('common.saving', 'Guardando...') : t('socialLinks.save', 'Guardar Redes Sociales')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default EditProfilePage;
